import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { SECTION_ALIAS } from '../../../constants/OneRealMortgageConstants';
import useDeviceType from '../../../hooks/useDeviceType';
import {
  AnswerApplicationQuestionRequest,
  FieldObjectTypeEnum,
  Question,
} from '../../../openapi/atlantis';
import { ConfigDrivenQuestionTemplate, PartialEnumMap } from '../../../types';
import {
  ApplicationStepWithNewContent,
  getFormDefaultValues,
} from '../../../utils/ConfigDrivenFormUtils';
import { safePartialEnumMapGet } from '../../../utils/EnumHelper';
import { cn } from '../../../utils/classUtils';
import ConfigDrivenContainerLayoutV2 from '../../Layout/ConfigDrivenContainerLayoutV2';
import ConfigDrivenFormV2 from '../Form/V2/ConfigDrivenFormV2';
import AgentConfigDrivenSubtitle from '../Misc/AgentConfigDrivenSubtitle';
import AgentConfigDrivenTitle from '../Misc/AgentConfigDrivenTitle';
import ConfigDrivenSubtitleV2 from '../Misc/ConfigDrivenSubtitleV2';
import ConfigDrivenTitleV2 from '../Misc/ConfigDrivenTitleV2';

interface ConfigDrivenQuestionContainerV2Props {
  config: ApplicationStepWithNewContent<Question>;
  onNext: () => Promise<void>;
  onSubmit: (formData: AnswerApplicationQuestionRequest) => Promise<void>;
  onNavigateToQuestion?: (questionId: string) => Promise<void>;
}

// Component maps for template-specific components
const TITLE_COMPONENTS: PartialEnumMap<
  ConfigDrivenQuestionTemplate,
  React.FC<{ title: string; textStyle?: string }>
> = {
  [ConfigDrivenQuestionTemplate.TEMPLATE_3]: AgentConfigDrivenTitle,
};

const SUBTITLE_COMPONENTS: PartialEnumMap<
  ConfigDrivenQuestionTemplate,
  React.FC<{ subtitle: string; textStyle?: string }>
> = {
  [ConfigDrivenQuestionTemplate.TEMPLATE_3]: AgentConfigDrivenSubtitle,
};

const ConfigDrivenQuestionContainerV2: React.FC<
  ConfigDrivenQuestionContainerV2Props
> = ({ config, onNext, onSubmit, onNavigateToQuestion }) => {
  const { isMobile } = useDeviceType();
  const { title, subtitle } = config?.content || {};
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Select appropriate components based on template
  const TitleComponent = safePartialEnumMapGet(
    TITLE_COMPONENTS,
    config?.content?.style?.template as unknown as ConfigDrivenQuestionTemplate,
    ConfigDrivenTitleV2,
  );

  const SubtitleComponent = safePartialEnumMapGet(
    SUBTITLE_COMPONENTS,
    config?.content?.style?.template as unknown as ConfigDrivenQuestionTemplate,
    ConfigDrivenSubtitleV2,
  );

  const methods = useForm({
    mode: 'onChange',
    defaultValues: getFormDefaultValues(config, true),
  });

  useEffect(() => {
    if (!isSubmitting) {
      methods.reset(getFormDefaultValues(config, true));
    }
  }, [config, isSubmitting, methods]);

  const isFieldTypeSignature = useMemo(() => {
    return config?.content?.fields?.some(
      (field) => field.type === FieldObjectTypeEnum.SignatureRequest,
    );
  }, [config?.content?.fields]);

  const isReviewApplication = useMemo(() => {
    return config?.alias === SECTION_ALIAS.REVIEW_APPLICATION;
  }, [config?.alias]);

  const isApplicationFee = useMemo(() => {
    return config?.alias === SECTION_ALIAS.APPLICATION_FEE;
  }, [config?.alias]);

  return (
    <ConfigDrivenContainerLayoutV2
      config={config}
      onNavigateToQuestion={onNavigateToQuestion}
    >
      <FormProvider {...methods}>
        <div
          className={cn(
            'flex flex-col w-full mx-auto',
            !isFieldTypeSignature && 'h-full overflow-y-auto',
            !isFieldTypeSignature && !isReviewApplication && 'sm:max-w-[524px]',
            isReviewApplication && 'sm:max-w-[900px]',
            isMobile && 'mb-10',
          )}
        >
          <div
            className={cn(
              'flex flex-col flex-grow',
              !isFieldTypeSignature &&
                !isReviewApplication &&
                'sm:items-center sm:justify-center pb-10',
            )}
          >
            <div className='mb-4 w-full'>
              {!!title && !isReviewApplication && !isApplicationFee && (
                <TitleComponent
                  title={title}
                  textStyle={
                    isFieldTypeSignature
                      ? 'flex justify-center'
                      : 'flex justify-start'
                  }
                />
              )}
              {!!subtitle && !isReviewApplication && (
                <div className='pt-2'>
                  <SubtitleComponent subtitle={subtitle} />
                </div>
              )}
            </div>
            <ConfigDrivenFormV2
              config={config}
              onNext={onNext}
              onSubmit={onSubmit}
              onNavigateToQuestion={onNavigateToQuestion}
              setIsSubmitting={setIsSubmitting}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      </FormProvider>
    </ConfigDrivenContainerLayoutV2>
  );
};

export default ConfigDrivenQuestionContainerV2;
